import { useState, useEffect, useRef } from "react";
import "./LoginPage.css";

import { Link } from "react-router-dom";
import mkcgLogo from "../assests/logo/logo.png";

const API_URL =
  process.env.REACT_APP_API_URL || "http://localhost:5001/api";

/* ─────────────────────────────────────────────────────────────
   Captcha generator
───────────────────────────────────────────────────────────── */
function generateCaptcha() {
  const chars =
    "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789";

  return Array.from(
    { length: 6 },
    () => chars[Math.floor(Math.random() * chars.length)]
  ).join("");
}

/* ─────────────────────────────────────────────────────────────
   Eye icon (show / hide password)
───────────────────────────────────────────────────────────── */
const EyeIcon = ({ open }) =>
  open ? (
    <svg
      width="17"
      height="17"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    >
      <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94" />
      <path d="M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  ) : (
    <svg
      width="17"
      height="17"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    >
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );

/* ─────────────────────────────────────────────────────────────
   Captcha Canvas
───────────────────────────────────────────────────────────── */
const CaptchaCanvas = ({ text }) => {
  const ref = useRef(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");

    ctx.clearRect(0, 0, 150, 46);

    ctx.fillStyle = "#f5f8fc";
    ctx.fillRect(0, 0, 150, 46);

    /* Lines */
    for (let i = 0; i < 5; i++) {
      ctx.strokeStyle = `rgba(13,110,253,${0.08 + Math.random() * 0.1})`;
      ctx.beginPath();
      ctx.moveTo(Math.random() * 150, Math.random() * 46);
      ctx.lineTo(Math.random() * 150, Math.random() * 46);
      ctx.stroke();
    }

    /* Dots */
    for (let i = 0; i < 30; i++) {
      ctx.fillStyle = `rgba(13,110,253,${Math.random() * 0.1})`;
      ctx.fillRect(Math.random() * 150, Math.random() * 46, 2, 2);
    }

    const fonts = ["Georgia", "serif", "cursive"];

    const colors = ["#0d6efd", "#063970", "#2985d5", "#174a7c"];

    [...text].forEach((ch, i) => {
      ctx.save();

      ctx.font = `bold ${22 + Math.random() * 6}px ${
        fonts[i % fonts.length]
      }`;

      ctx.fillStyle = colors[i % colors.length];

      ctx.translate(10 + i * 23, 29 + Math.random() * 6 - 3);

      ctx.rotate((Math.random() - 0.5) * 0.35);

      ctx.fillText(ch, 0, 0);

      ctx.restore();
    });
  }, [text]);

  return (
    <canvas
      ref={ref}
      width={150}
      height={46}
      className="lp-captcha-canvas"
    />
  );
};

/* ─────────────────────────────────────────────────────────────
   Main Student Login (new split-screen design + full logic)
───────────────────────────────────────────────────────────── */
const LoginPage = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [captchaInput, setCaptchaInput] = useState("");
  const [captcha, setCaptcha] = useState(generateCaptcha);
  const [showPw, setShowPw] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  /* Refresh captcha */
  const refreshCaptcha = () => {
    setCaptcha(generateCaptcha());
    setCaptchaInput("");
  };

  /* Validation */
  const validate = () => {
    const e = {};

    if (!email) {
      e.email = "Email address is required.";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      e.email = "Enter a valid email address.";
    }

    if (!password) {
      e.password = "Password is required.";
    } else if (password.length < 6) {
      e.password = "Password must be at least 6 characters.";
    }

    if (!captchaInput) {
      e.captcha = "Please enter the captcha.";
    } else if (captchaInput !== captcha) {
      e.captcha = "Captcha does not match. Try again.";
      refreshCaptcha();
    }

    return e;
  };

  /* Login submit */
  const handleSubmit = async (e) => {
    e.preventDefault();

    setErrors({});

    const errs = validate();

    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }

    try {
      setLoading(true);

      const response = await fetch(`${API_URL}/student/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email,
          password,
        }),
      });

      const data = await response.json();

      setLoading(false);

      if (data.success) {
        localStorage.setItem("student", JSON.stringify(data));

        setSuccess(true);

        setTimeout(() => {
          window.location.href = "/student-registration-new";
        }, 1500);
      } else {
        setErrors({
          login: data.message || "Login failed. Please try again.",
        });

        refreshCaptcha();
      }
    } catch (err) {
      setLoading(false);

      console.error(err);

      setErrors({
        login: "Cannot connect to server. Please try again.",
      });
    }
  };

  /* Input change */
  const handleChange = (field, val) => {
    if (field === "email") setEmail(val);
    if (field === "password") setPassword(val);
    if (field === "captcha") setCaptchaInput(val);

    if (errors[field] || errors.login) {
      setErrors((p) => ({
        ...p,
        [field]: undefined,
        login: undefined,
      }));
    }
  };

  return (
    <div className="login-page">
      <div className="login-container">
        {/* Left Section */}
        <section className="subscribe-section">
          <div className="subscribe-content">
            <img
              src={mkcgLogo}
              alt="MKCG Medical College"
              style={{ width: "100px", height: "100px", marginLeft: "115px" }}
            />
            <br />
            <h2>
              M.K.C.G. MEDICAL COLLEGE <br />
              <span style={{ fontSize: "18px" }}>
                BERHAMPUR, ODISHA, INDIA
              </span>
            </h2>

            <p className="ig-side-odia" style={{ fontSize: "18px" }}>
              ମହାରାଜା କୃଷ୍ଣଚନ୍ଦ୍ର ଗଜପତି ଭେଷଜ ମହାବିଦ୍ୟାଳୟ,
              <br />
              ବ୍ରହ୍ମପୁର, ଓଡ଼ିଶା, ଭାରତ
            </p>
          </div>

          <Link to="/" className="go-button">
            <span>Return to Home</span>
            <span className="arrow">→</span>
          </Link>
        </section>

        {/* Right Section */}
        <section className="signin-section">
          <div className="signin-content">
            {success ? (
              /* SUCCESS STATE */
              <div className="lp-success-card">
                <div className="lp-success-ring">
                  <div className="lp-success-icon">✓</div>
                </div>

                <h2 className="lp-success-title">Login Successful!</h2>

                <p className="lp-success-sub">
                  Welcome back. Redirecting to registration…
                </p>

                <div className="lp-spinner-bar" />
              </div>
            ) : (
              <>
                <h1>Student Login</h1>
                <br />

                {errors.login && (
                  <div className="lp-login-error">
                    <svg
                      width="15"
                      height="15"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <circle cx="12" cy="12" r="10" />
                      <line x1="12" y1="8" x2="12" y2="12" />
                      <line x1="12" y1="16" x2="12.01" y2="16" />
                    </svg>
                    {errors.login}
                  </div>
                )}

                <form onSubmit={handleSubmit} noValidate>
                  {/* EMAIL */}
                  <div className="field">
                    <label htmlFor="email">Email ID</label>
                    <input
                      id="email"
                      type="email"
                      placeholder="Enter Mail ID"
                      value={email}
                      onChange={(e) =>
                        handleChange("email", e.target.value)
                      }
                      autoComplete="email"
                    />
                    {errors.email && (
                      <p className="lp-error-msg">⚠ {errors.email}</p>
                    )}
                  </div>

                  {/* PASSWORD */}
                  <div className="field">
                    <label htmlFor="password">Password</label>
                    <div className="lp-password-wrap">
                      <input
                        id="password"
                        type={showPw ? "text" : "password"}
                        placeholder="Enter Password"
                        value={password}
                        onChange={(e) =>
                          handleChange("password", e.target.value)
                        }
                        autoComplete="current-password"
                      />
                      <button
                        type="button"
                        className="lp-eye"
                        onClick={() => setShowPw((v) => !v)}
                        tabIndex={-1}
                      >
                        <EyeIcon open={showPw} />
                      </button>
                    </div>
                    {errors.password && (
                      <p className="lp-error-msg">⚠ {errors.password}</p>
                    )}
                  </div>

                  {/* CAPTCHA */}
                  <div className="field">
                    <label>Captcha</label>

                    <div className="lp-captcha-row">
                      <CaptchaCanvas text={captcha} />

                      <button
                        type="button"
                        className="lp-refresh-btn"
                        onClick={refreshCaptcha}
                        title="Refresh captcha"
                      >
                        ↻
                      </button>

                      <input
                        id="captchaInput"
                        type="text"
                        placeholder="Enter Captcha"
                        value={captchaInput}
                        onChange={(e) =>
                          handleChange("captcha", e.target.value)
                        }
                        autoComplete="off"
                      />
                    </div>
                    {errors.captcha && (
                      <p className="lp-error-msg">⚠ {errors.captcha}</p>
                    )}
                  </div>

                  <button
                    type="submit"
                    className="signin-button"
                    disabled={loading}
                  >
                    {loading ? (
                      <span className="lp-btn-spinner" />
                    ) : (
                      <>
                        <span style={{ fontSize: "18px",marginLeft: "55px"}}>Login</span>
                        <span className="arrow">→</span>
                      </>
                    )}
                  </button>
                </form>

                <div className="bottom-links">
                  <span>
                    Don't have an account?{" "}
                    <Link to="/StudentRegistration">Register</Link>
                  </span>

                  <Link
                    to="/forgot-password"
                    style={{ color: "#c0392b", textDecoration: "none" }}
                  >
                    Forgot Password?
                  </Link>
                </div>
              </>
            )}
          </div>
        </section>
      </div>
    </div>
  );
};

export default LoginPage;
